"use client"

import { useEffect, useState, type ReactNode } from "react"
import Link from "next/link"
import { IlustrasiSlot } from "@/modules/media/ui/ilustrasi-slot"
import { HALAMAN_SISTEM, labelSisaWaktu, type AksiHalaman, type KodeHalamanSistem } from "@/modules/sistem/domain/halaman"
import { SocialMediaButton } from "@/modules/kontak/ui/social-media-button"
import { TombolBulat } from "@/modules/sistem/ui/tombol-bulat"
import { cn } from "@/shared/lib/utils"

interface HalamanSistemProps {
  kode: KodeHalamanSistem
  judul?: string
  deskripsi?: string
  aksi?: AksiHalaman[]
  detikTunggu?: number
  rujukan?: string
  onCobaLagi?: () => void
  tambahan?: ReactNode
  layarPenuh?: boolean
  tombolKontak?: boolean
}

export function HalamanSistem({ kode, judul, deskripsi, aksi, detikTunggu = 0, rujukan, onCobaLagi, tambahan, layarPenuh = true, tombolKontak = true }: HalamanSistemProps) {
  const konfigurasi = HALAMAN_SISTEM[kode]
  const [sisa, setSisa] = useState(detikTunggu)

  useEffect(() => {
    if (sisa <= 0) return
    const waktu = window.setTimeout(() => setSisa((nilai) => nilai - 1), 1000)
    return () => window.clearTimeout(waktu)
  }, [sisa])

  const jalankan = (item: AksiHalaman) => {
    if (item.aksi === "coba-lagi" && onCobaLagi) onCobaLagi()
    else if (item.aksi === "kembali") window.history.back()
    else window.location.reload()
  }

  const kodeAngka = /^\d+$/.test(konfigurasi.kode)

  return (
    <>
      <main className={cn("flex w-full items-center justify-center bg-background px-5 py-10", layarPenuh ? "min-h-dvh" : "min-h-[calc(100dvh-57px)]")}>
        <div className="grid w-full max-w-5xl items-center gap-8 md:grid-cols-[1.15fr_1fr] md:gap-12">
          <IlustrasiSlot kunci={konfigurasi.slot} alt={kodeAngka ? `Kode ${konfigurasi.kode}` : ""} prioritas className="max-h-[42dvh] max-w-md md:max-h-[68dvh] md:max-w-none" />

          <div className="space-y-5 text-center md:text-left">
            <div className="space-y-3">
              <h1 className="font-display text-2xl font-bold tracking-tight text-balance sm:text-3xl">{judul ?? konfigurasi.judul}</h1>
              <p className="mx-auto max-w-md text-sm leading-relaxed text-muted-foreground text-pretty sm:text-base md:mx-0">{deskripsi ?? konfigurasi.deskripsi}</p>
              {sisa > 0 && <p className="text-sm font-semibold text-primary" role="status">Bisa dicoba lagi dalam {labelSisaWaktu(sisa)}</p>}
            </div>

            <div className="flex items-center justify-center gap-3 md:justify-start">
              {(aksi ?? konfigurasi.aksi).map((item) => (
                <TombolBulat
                  key={item.label}
                  ikon={item.ikon ?? (item.href ? "beranda" : "muat-ulang")}
                  label={item.label}
                  utama={item.utama}
                  href={item.href}
                  onClick={item.href ? undefined : () => jalankan(item)}
                  nonaktif={Boolean(item.utama) && sisa > 0}
                  isi={item.utama && sisa > 0 && sisa < 100 ? sisa : undefined}
                />
              ))}
            </div>

            {tambahan}
            {rujukan && <p className="font-mono text-[11px] text-muted-foreground/70">Kode rujukan: {rujukan}</p>}
          </div>
        </div>
      </main>
      {tombolKontak && <SocialMediaButton />}
    </>
  )
}
